/* mf-sub-selector: the shared subscription selection component (Addendum 6
   §3, Session B). One logic core, two skins: the builder page's full form
   and the PDP subscribe modal both wrap this element around their own
   markup and speak to it through data attributes.

   Data contract: a child <script type="application/json" data-sub-products>
   holding an array of objects from snippets/mf-sub-data.liquid. Plans are
   recognised by name – "N deliveries, every 2 weeks|monthly|every 2 months"
   – and anything else (the legacy pre-rebuild plans included) is ignored.
   A plan without a percentage discount is dropped rather than priced by
   guesswork: every figure shown is live variant price × live plan discount
   (§3 honesty guards, §7 format via the same class names as the Liquid
   price-block snippet).

   Rules enforced here, both directions, per §3:
   - every-two-months disables at 12 deliveries, with a visible reason;
     if already selected it snaps to monthly.
   - a coffee carrying only 3-delivery plans disables 6 and 12, with a
     visible reason, and snaps the selection to 3.
   No timers, no urgency, no fabricated numbers. */
(() => {
  const FREQ_RX = /^(\d+)\s*deliveries?\s*,\s*(every\s*2\s*weeks|monthly|every\s*2\s*months)$/i;
  const FREQ_DAYS = { 'every2weeks': 14, monthly: 30, 'every2months': 60 };
  const FREQ_LABEL = { 14: 'Every two weeks', 30: 'Monthly', 60: 'Every two months' };
  const FREQ_SHORT = { 14: 'Two weeks', 30: 'Month', 60: 'Two months' };
  const FREQ_WORD = { 14: 'every two weeks', 30: 'monthly', 60: 'every two months' };
  /* (S5) Shopify selling plan ID -> Recurpay plan ID. Recurpay mirrors each
     of its plans into Shopify as a selling plan; the Recurpay ID is what its
     checkout needs. Keyed by selling plan ID because plan names repeat
     across the two plan groups. Read from Recurpay > Plans (13 Oct 2026).
     A plan added or recreated in Recurpay needs a line here, or the button
     shows the error message instead of checking out. */
  const RP_PLAN_IDS = {
    4195254439: '11152248', // 3 deliveries, every 2 weeks
    4195287207: '11152249', // 6 deliveries, every 2 weeks
    4195319975: '11152250', // 12 deliveries, every 2 weeks
    4195352743: '11152251', // 3 deliveries, monthly
    4195385511: '11152252', // 6 deliveries, monthly
    4195418279: '11152253', // 12 deliveries, monthly
    4195451047: '11152254', // 3 deliveries, every 2 months
    4195483815: '11152255', // 6 deliveries, every 2 months
    4195680423: '11152256', // 3 deliveries, every 2 weeks (3-delivery group)
    4195713191: '11152257', // 3 deliveries, monthly (3-delivery group)
    4195745959: '11152258', // 3 deliveries, every 2 months (3-delivery group)
  };
  const RP_CHECKOUT = 'https://api.recurpay.com/api/storefront/v3/checkout/initiate.rp';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  class MfSubSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.subInit) return;
      this.dataset.subInit = 'true';

      const payload = this.querySelector('script[data-sub-products]');
      let products = [];
      try { products = JSON.parse(payload.textContent); } catch (e) { /* no data, render nothing */ }

      // Parse plans once; keep only products with at least one usable plan.
      products.forEach((p) => {
        p.parsed = [];
        (p.plans || []).forEach((pl) => {
          const m = FREQ_RX.exec(String(pl.name).trim());
          if (!m) return;
          const freq = FREQ_DAYS[norm(m[2])];
          const pct = Number(pl.pct) || 0;
          if (!freq || pct <= 0) return;
          p.parsed.push({ id: pl.id, n: Number(m[1]), freq: freq, pct: pct });
        });
        p.tiers = [...new Set(p.parsed.map((x) => x.n))].sort((a, b) => a - b);
      });
      /* (15 Oct) Packs sold by the cup (Brewin'-a-cup brew bags, flagged by
         custom.cups_per_pack) subscribe from their own page only: the
         builder's grind and size steps do not apply to them. The builder
         is the skin with a coffee select. */
      const isBuilder = !!this.querySelector('select[data-sub-control="coffee"]');
      this.products = products.filter((p) => p.parsed.length > 0 && !(isBuilder && Number(p.cupsPerPack) > 0));
      if (!this.products.length) { this.hidden = true; return; }

      /* Coffee select housekeeping: drop options whose product carries no
         usable plan; empty groups go with them. A group whose remaining
         coffees are ALL 3-delivery-only gets the note appended to its
         label – derived from the data, never from a list in code. */
      const cofSel = this.querySelector('select[data-sub-control="coffee"]');
      if (cofSel) {
        [...cofSel.querySelectorAll('optgroup')].forEach((g) => {
          [...g.querySelectorAll('option')].forEach((o) => {
            if (!this.products.some((p) => p.handle === o.value)) o.remove();
          });
          const rest = [...g.querySelectorAll('option')];
          if (!rest.length) { g.remove(); return; }
          const allThree = rest.every((o) => {
            const p = this.products.find((pp) => pp.handle === o.value);
            return p.tiers.length === 1 && p.tiers[0] === 3;
          });
          if (allThree && cofSel.dataset.threeOnlyNote) g.label += ' · ' + cofSel.dataset.threeOnlyNote;
        });
      }

      this.cupG = Number(this.dataset.cupGrams) || 16;
      this.moneyFormat = this.dataset.moneyFormat || 'Rs. {{amount}}';
      this.sizeHints = {};
      try { this.sizeHints = JSON.parse(this.dataset.sizeHints || '{}'); } catch (e) { /* optional */ }

      // Initial state, then URL pre-fills (§1 contract: coffee, n, size, freq, grind).
      const first = this.products[0];
      this.state = { handle: first.handle, grind: null, size: null, freq: 30, n: 6, who: 'me' };
      const q = new URLSearchParams(window.location.search);
      if (this.dataset.prefill !== 'off') {
        const want = q.get('coffee');
        if (want && this.products.some((p) => p.handle === want)) this.state.handle = want;
        const n = Number(q.get('n'));
        if ([3, 6, 12].includes(n)) this.state.n = n;
        const f = Number(q.get('freq'));
        if ([14, 30, 60].includes(f)) this.state.freq = f;
        if (q.get('size')) this.state.size = q.get('size');
        if (q.get('grind')) this.state.grind = q.get('grind');
        /* The §3 constraint applies to pre-fills too: a 12-delivery
           pre-fill with a bimonthly one snaps to monthly, not to whatever
           plan happens to sort first. */
        if (this.state.n === 12 && this.state.freq === 60) this.state.freq = 30;
      }

      this.bind();
      this.applyProduct(true);
    }

    get product() { return this.products.find((p) => p.handle === this.state.handle); }

    /* Reset per-product choices: sizes and grinds come from the product's
       own option values, never from a list in code. */
    applyProduct(initial) {
      const p = this.product;
      const sizeWant = norm(this.state.size);
      const sizeWantG = parseInt(this.state.size, 10);
      this.state.size =
        p.sizes.find((s) => norm(s) === sizeWant) ||
        p.sizes.find((s) => this.grams(s) === sizeWantG) ||
        p.sizes[0];
      const grindWant = norm(this.state.grind);
      this.state.grind =
        p.grinds.find((g) => norm(g) === grindWant) ||
        p.grinds.find((g) => norm(g).indexOf('whole') === 0) ||
        p.grinds[0] || null;
      if (!p.tiers.includes(this.state.n)) this.state.n = p.tiers[0];
      this.buildChoices();
      this.render(initial);
    }

    variantFor(size, grind) {
      return this.product.variants.find((v) => v.size === size && (this.product.grinds.length === 0 || v.grind === grind));
    }

    planFor(n, freq) { return this.product.parsed.find((x) => x.n === n && x.freq === freq); }

    money(paise) {
      const noDec = /no_decimals/.test(this.moneyFormat);
      const rupees = paise / 100;
      const amount = noDec
        ? Math.round(rupees).toLocaleString('en-IN')
        : (Math.round(rupees * 100) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
      return this.moneyFormat.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, amount);
    }

    grams(sizeValue) { const m = /(\d+)/.exec(sizeValue); return m ? Number(m[1]) : 0; }
    sizeLabel(sizeValue) { const g = this.grams(sizeValue); return g >= 1000 && g % 1000 === 0 ? g / 1000 + 'kg' : sizeValue; }

    /* Build the per-product choice controls into whichever containers this
       skin provides: pill groups (builder) or selects (modal). */
    buildChoices() {
      const p = this.product;
      const mk = (group, items) => {
        const pillBox = this.querySelector('[data-sub-pills="' + group + '"]');
        if (pillBox) {
          pillBox.textContent = '';
          items.forEach((it) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = this.dataset.pillClass || 'mf-subsel__pill';
            b.dataset.subChoice = group;
            b.dataset.subValue = it.value;
            b.innerHTML = it.html;
            pillBox.appendChild(b);
          });
        }
        const sel = this.querySelector('select[data-sub-control="' + group + '"]');
        if (sel) {
          sel.textContent = '';
          items.forEach((it) => {
            const o = document.createElement('option');
            o.value = it.value;
            o.textContent = it.label;
            sel.appendChild(o);
          });
        }
      };

      mk('grind', p.grinds.map((g) => ({ value: g, label: g, html: this.esc(g) })));
      /* (15 Oct) A product with no real size or grind option (one variant,
         'Default Title') hides those controls instead of showing an empty
         or meaningless choice. */
      const noChoice = (vals) => vals.length === 0 || (vals.length === 1 && /^default title$/i.test(String(vals[0])));
      [['size', p.sizes], ['grind', p.grinds]].forEach(([key, vals]) => {
        const hide = noChoice(vals);
        this.querySelectorAll('select[data-sub-control="' + key + '"], [data-sub-pills="' + key + '"]').forEach((el) => {
          const box = el.closest('label') || el;
          box.hidden = hide;
          box.style.display = hide ? 'none' : '';
        });
      });
      mk('size', p.sizes.map((s) => {
        const hint = this.sizeHints[String(this.grams(s))] || '';
        return {
          value: s,
          label: this.sizeLabel(s),
          html: '<b>' + this.esc(this.sizeLabel(s)) + '</b><span data-sub-size-price data-size="' + this.esc(s) + '"></span>' + (hint ? '<span class="mf-subsel__hint-line">' + this.esc(hint) + '</span>' : ''),
        };
      }));
      mk('freq', [14, 30, 60].map((f) => ({ value: String(f), label: FREQ_LABEL[f], html: this.esc(FREQ_LABEL[f]) + '<small data-sub-freq-note hidden></small>' })));
      mk('n', [3, 6, 12].map((n) => {
        const best = Math.max(0, ...p.parsed.filter((x) => x.n === n).map((x) => x.pct));
        return { value: String(n), label: n + ' deliveries' + (best ? ' · save ' + best + '%' : ''), html: '<b>' + n + '</b><span>deliveries</span>' + (best ? '<span class="mf-subsel__off">Save ' + best + '%</span>' : '') };
      }));
      mk('who', [{ value: 'me', label: 'For me', html: 'For me' }, { value: 'gift', label: 'It is a gift', html: 'It is a gift' }]);
    }

    esc(s) { const d = document.createElement('div'); d.textContent = String(s); return d.innerHTML; }

    bind() {
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-sub-choice]');
        if (!b || b.disabled) return;
        this.set(b.dataset.subChoice, b.dataset.subValue);
      });
      this.addEventListener('change', (e) => {
        const c = e.target.closest('[data-sub-control]');
        if (c) this.set(c.dataset.subControl, c.value);
      });
      const submit = this.querySelector('[data-sub-submit]');
      if (submit) submit.addEventListener('click', () => this.subscribe());

      /* (S26 item 5) Mobile summary expansion – builder page only (the
         PDP modal has no expand button, so this wires nothing there).
         The chevron toggles the detail rows as an overlay above the page;
         the scrim behind it collapses on tap. Animation lives in CSS and
         is suppressed under prefers-reduced-motion there. */
      const expand = this.querySelector('[data-sub-expand]');
      if (expand) {
        const sum = expand.closest('.mf-subb__sum');
        const scrim = this.querySelector('[data-sub-scrim]');
        const setOpen = (open) => {
          sum.classList.toggle('is-open', open);
          expand.setAttribute('aria-expanded', String(open));
          expand.setAttribute('aria-label', open ? 'Hide full summary' : 'Show full summary');
          if (scrim) scrim.hidden = !open;
        };
        expand.addEventListener('click', () => setOpen(!sum.classList.contains('is-open')));
        if (scrim) scrim.addEventListener('click', () => setOpen(false));
      }
    }

    set(key, value) {
      if (key === 'coffee') {
        if (!this.products.some((p) => p.handle === value)) return;
        this.state.handle = value;
        this.applyProduct();
        return;
      }
      if (key === 'n' || key === 'freq') value = Number(value);
      this.state[key === 'coffee' ? 'handle' : key] = value;

      /* §3 constraints, applied on every change with visible reasons. */
      if (this.state.n === 12 && this.state.freq === 60) this.state.freq = 30;
      if (this.product.tiers.length && !this.product.tiers.includes(this.state.n)) this.state.n = this.product.tiers[0];
      this.render();
    }

    render(initial) {
      const p = this.product;
      const st = this.state;
      const variant = this.variantFor(st.size, st.grind);
      const plan = this.planFor(st.n, st.freq) ||
        p.parsed.find((x) => x.n === st.n && x.freq === 30) ||
        p.parsed.find((x) => x.n === st.n) || p.parsed[0];
      if (plan && plan.freq !== st.freq) st.freq = plan.freq;
      if (plan && plan.n !== st.n) st.n = plan.n;

      // Selected states on pills and selects.
      this.querySelectorAll('[data-sub-choice]').forEach((b) => {
        const cur = String(st[b.dataset.subChoice === 'coffee' ? 'handle' : b.dataset.subChoice]);
        b.classList.toggle('is-on', b.dataset.subValue === cur);
      });
      this.querySelectorAll('select[data-sub-control]').forEach((s) => {
        const key = s.dataset.subControl;
        s.value = String(st[key === 'coffee' ? 'handle' : key]);
      });

      // Disable + reasons: bimonthly at 12; tiers the coffee does not carry.
      const three_only = p.tiers.length === 1 && p.tiers[0] === 3;
      this.querySelectorAll('[data-sub-choice="freq"], select[data-sub-control="freq"] option').forEach((el) => {
        const v = Number(el.dataset ? el.dataset.subValue || el.value : el.value);
        el.disabled = v === 60 && st.n === 12;
      });
      this.querySelectorAll('[data-sub-choice="n"], select[data-sub-control="n"] option').forEach((el) => {
        const v = Number(el.dataset ? el.dataset.subValue || el.value : el.value);
        el.disabled = p.tiers.length > 0 && !p.tiers.includes(v);
      });
      const freqReason = this.querySelector('[data-sub-reason="freq"]');
      if (freqReason) freqReason.hidden = st.n !== 12;
      const nReason = this.querySelector('[data-sub-reason="n"]');
      if (nReason) nReason.hidden = !three_only;

      // Confirmation card (builder step 1).
      const img = this.querySelector('[data-sub-confirm-image]');
      if (img) { if (p.image) { img.src = p.image; img.alt = p.title; img.hidden = false; } else { img.hidden = true; } }
      const nm = this.querySelector('[data-sub-confirm-name]');
      if (nm) nm.textContent = p.title;
      const nt = this.querySelector('[data-sub-confirm-note]');
      if (nt) { nt.textContent = p.note || ''; nt.hidden = !p.note; }
      const rl = this.querySelector('[data-sub-confirm-roast]');
      if (rl) {
        rl.hidden = !(p.roast >= 1 && p.roast <= 5);
        rl.querySelectorAll('i').forEach((pip, i) => pip.classList.toggle('on', i < p.roast));
        if (p.roastLabel) rl.setAttribute('aria-label', p.roastLabel + ' – roast level ' + p.roast + ' of 5');
      }

      // Size-step hint computed from THIS product's sizes at the cup
      // standard – never a fixed 250/500/1kg list (S22 item 4: Ol' Smoky
      // sells in 200g only, and the step must say so, not invent sizes).
      const sizeHint = this.querySelector('[data-sub-size-hint]');
      if (sizeHint) {
        const parts = p.sizes
          .map((sz) => ({ label: this.sizeLabel(sz), g: this.grams(sz) }))
          .filter((x) => x.g > 0)
          .map((x, i) => x.label + (i === 0 ? ' is about ' : ' about ') + Math.floor(x.g / this.cupG) + (i === 0 ? ' cups' : ''));
        /* floor, not round: the founders' cup standard (§0) is 250g ≈ 15,
           500g ≈ 31, 1kg ≈ 62 – all floors of grams/16. All copy uses
           these figures, so the computed line must land on them. */
        sizeHint.textContent = parts.length ? 'At ' + this.cupG + 'g a cup: ' + parts.join(', ') + '.' : '';
        sizeHint.hidden = !parts.length;
      }

      // Per-size prices for the current grind (§3 step 3). "/ bag" so the
      // figure reads as the bag price, not a total (S26 item 4).
      this.querySelectorAll('[data-sub-size-price]').forEach((el) => {
        const v = this.variantFor(el.dataset.size, st.grind);
        el.textContent = v ? this.money(v.price) + ' / bag' : '';
      });

      // Summary fields.
      const out = (k, v) => { const el = this.querySelector('[data-sub-out="' + k + '"]'); if (el) el.textContent = v; };
      out('coffee', p.title);
      out('grind', st.grind || '—');
      out('size', this.sizeLabel(st.size));
      out('freq', FREQ_SHORT[st.freq]);
      out('n', String(st.n));
      out('who', st.who === 'gift' ? 'A gift' : 'You');
      const sumImg = this.querySelector('[data-sub-summary-image]');
      if (sumImg) { if (p.image) { sumImg.src = p.image; sumImg.alt = p.title; sumImg.hidden = false; } else { sumImg.hidden = true; } }

      // Gift fields.
      const gift = this.querySelector('[data-sub-gift]');
      if (gift) gift.hidden = st.who !== 'gift';

      // §7 price block – same arithmetic as snippets/mf-sub-price-block.liquid.
      const priceBox = this.querySelector('[data-sub-price]');
      if (priceBox && variant && plan) {
        const full = variant.price * plan.n;
        const total = Math.round(full * (100 - plan.pct) / 100);
        const saving = full - total;
        const perBagRaw = total / plan.n;
        const perBag = Math.round(perBagRaw / 100) * 100;
        const set = (k, v, show) => {
          const el = priceBox.querySelector('[data-sub-price-' + k + ']');
          if (el) { el.textContent = v; if (show !== undefined) el.hidden = !show; }
        };
        set('total', this.money(total));
        set('was', this.money(full), saving > 0);
        set('saving', 'You save ' + this.money(saving), saving > 0);
        /* (15 Oct) Packs sold by the cup price per pack and per cup from
           custom.cups_per_pack, not from shipping weight. */
        const cupsPerPack = Number(p.cupsPerPack) || 0;
        let unit = this.money(perBag) + (cupsPerPack > 0 ? ' a pack' : ' a bag');
        if (cupsPerPack > 0) {
          const perCup = Math.round(perBagRaw / cupsPerPack / 100) * 100;
          unit += ' · ' + this.money(perCup) + ' a cup';
        } else if (variant.weight > 0) {
          const perCup = Math.round((perBagRaw * this.cupG) / variant.weight / 100) * 100;
          unit += ' · ' + this.money(perCup) + ' a cup';
        }
        set('unit', unit);
        priceBox.hidden = false;
      } else if (priceBox) {
        priceBox.hidden = true;
      }

      // Delivery schedule from today and the frequency (§3 summary).
      const sched = this.querySelector('[data-sub-out="sched"]');
      if (sched && plan) {
        const start = new Date();
        start.setDate(start.getDate() + 1);
        const stops = [];
        for (let i = 1; i < plan.n; i++) {
          const d = new Date(start);
          d.setDate(d.getDate() + plan.freq * i);
          stops.push(MONTHS[d.getMonth()]);
        }
        const shown = stops.slice(0, 5).join(', ') + (stops.length > 5 ? ' …' : '');
        sched.textContent = 'First bag roasted the day after you order. Then ' + FREQ_WORD[plan.freq] + (shown ? ': ' + shown + '.' : '.');
      }

      const submit = this.querySelector('[data-sub-submit]');
      if (submit) submit.disabled = !(variant && variant.available && plan);

      this.dispatchEvent(new CustomEvent('mf-sub:render', { detail: { state: { ...st } } }));
    }

    /* (S5) Subscriptions check out on Recurpay, never on Shopify: Shopify
       checkout cannot take Razorpay payments for selling-plan items.
       The button posts the chosen variant and Recurpay plan straight to
       Recurpay's checkout, the same request Recurpay's own cart script
       builds (recurpay.recurCart). Nothing goes into the Shopify cart, so
       the Recurpay app embed and its PDP widget are not needed. If a plan
       has no Recurpay ID mapped, the button refuses rather than sending
       the customer to a checkout that would fail. */
    subscribe() {
      const st = this.state;
      const variant = this.variantFor(st.size, st.grind);
      const plan = this.planFor(st.n, st.freq);
      if (!variant || !plan) return;
      const submit = this.querySelector('[data-sub-submit]');
      const err = this.querySelector('[data-sub-error]');
      const rpPlanId = RP_PLAN_IDS[String(plan.id)];
      const shop = window.Shopify && window.Shopify.shop;
      if (!rpPlanId || !shop) {
        if (err) { err.hidden = false; }
        return;
      }
      if (err) { err.hidden = true; }
      if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }

      const properties = [];
      if (st.who === 'gift') {
        const gname = this.querySelector('[data-sub-gift-name]');
        const gnote = this.querySelector('[data-sub-gift-note]');
        if (gname && gname.value.trim()) properties.push({ name: 'Gift for', value: gname.value.trim() });
        if (gnote && gnote.value.trim()) properties.push({ name: 'Gift note', value: gnote.value.trim() });
      }
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page;
      const customer = meta && meta.customerId ? String(meta.customerId) : 'guest';
      const lineItems = [{
        quantity: 1,
        variant_id: variant.id,
        properties: properties,
        plan_id: rpPlanId,
        type: 'SUBSCRIPTION',
        currency: 'INR',
      }];

      const form = document.createElement('form');
      form.method = 'post';
      form.action = RP_CHECKOUT + '?platform=shopify&shop=' + encodeURIComponent(shop);
      form.style.display = 'none';
      const field = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };
      field('line_items', JSON.stringify(lineItems));
      field('customer_id', customer);
      field('note_attributes', '[]');
      field('note', '');
      document.body.appendChild(form);
      form.submit();
    }
  }

  /* Back button from Recurpay's checkout restores this page from the
     back-forward cache with the button still busy; release it. */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('mf-sub-selector [data-sub-submit][aria-busy]').forEach((b) => {
      b.disabled = false;
      b.removeAttribute('aria-busy');
    });
  });

  if (!customElements.get('mf-sub-selector')) customElements.define('mf-sub-selector', MfSubSelector);
})();
